<template>
  <div class="products-manager">
    <!-- Barre d'outils -->
    <div class="toolbar">
      <div class="toolbar-left">
        <div class="search-box">
          <i class="fas fa-search"></i>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Rechercher un produit..."
            @input="debouncedSearch"
            class="search-input"
          />
          <button v-if="searchQuery" @click="clearSearch" class="clear-search">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="filters">
          <div class="filter-wrapper filter-categories" :class="getCategorieClass">
            <div class="filter-icon">
              <i class="fas fa-layer-group"></i>
            </div>
            <select v-model="filters.categorie" @change="applyFilters" class="filter-select premium-select">
              <option value="">Toutes catégories</option>
              <option v-if="loadingCategories" disabled>Chargement...</option>
              <option 
                v-for="cat in categories" 
                :key="cat._id" 
                :value="cat.nom"
              >
                {{ cat.emoji }} {{ cat.nom }}
              </option>
            </select>
            <div class="filter-arrow">
              <i class="fas fa-chevron-down"></i>
            </div>
          </div>
          
          <div class="filter-wrapper filter-status" :class="getStatutClass">
            <div class="filter-icon">
              <i class="fas fa-flag"></i>
            </div>
            <select v-model="filters.statut" @change="applyFilters" class="filter-select premium-select">
              <option value="">Tous statuts</option>
              <option value="en_stock">✅ En stock</option>
              <option value="affecte_technicien">👨‍🔧 Affecté technicien</option>
              <option value="en_cours">🔧 En cours</option>
              <option value="installe">🏠 Installé</option>
              <option value="retourne">↩️ Retourné</option>
              <option value="defectueux">🔧 Détérioré</option>
              <option value="perdu">🚨 Volé</option>
            </select>
            <div class="filter-arrow">
              <i class="fas fa-chevron-down"></i>
            </div>
          </div>
        </div>
      </div>
      
      <div class="toolbar-right">
        <!-- Boutons de vue -->
        <div class="view-toggle">
          <button 
            @click="viewMode = 'table'" 
            :class="['view-btn', { active: viewMode === 'table' }]"
            title="Vue tableau"
          >
            <i class="fas fa-table"></i>
          </button>
          <button 
            @click="viewMode = 'cards'" 
            :class="['view-btn', { active: viewMode === 'cards' }]"
          >
            <i class="fas fa-th-large"></i>
          </button>
        </div>
        
        <button @click="exportProducts" class="btn btn-export">
          <i class="fas fa-download"></i>
          Exporter
        </button>
        <button 
          v-if="userRole === 'Magasinier' || userRole === 'Admin'"
          @click="$emit('give-products')" 
          class="btn btn-secondary"
        >
          <i class="fas fa-user-cog"></i>
          Donner Produits au Technicien
        </button>
        <button 
          v-if="userRole === 'Magasinier' || userRole === 'Admin'"
          @click="showDeclarationsPanel = !showDeclarationsPanel" 
          class="btn btn-warning"
          :class="{ active: showDeclarationsPanel }"
        >
          <i class="fas fa-exclamation-triangle"></i>
          Déclarations
          <span v-if="pendingDeclarationsCount > 0" class="badge-count">{{ pendingDeclarationsCount }}</span>
        </button>
        <button 
          v-if="userRole === 'Admin'"
          @click="showBulkModal = true" 
          class="btn btn-bulk"
          title="Ajouter plusieurs produits avec des IMEI/ICCID différents"
        >
          <i class="fas fa-layer-group"></i>
          Ajout en Lot
        </button>
        <router-link 
          v-if="userRole === 'Magasinier' || userRole === 'Admin'"
          to="/achats"
          class="btn btn-success"
          title="Saisir une Facture d'Achat pour ajouter du Stock"
        >
          <i class="fas fa-file-invoice-dollar me-1"></i>
          Entrée Achat (Facture)
        </router-link>
        <button 
          v-if="userRole === 'Admin'"
          @click="$emit('create')" 
          class="btn btn-primary"
        >
          <i class="fas fa-plus"></i>
          Fiche Produit
        </button>
      </div>
    </div>


    <!-- Contenu des produits -->
    <div class="content-container">
      <div v-if="loading" class="loading-overlay">
        <div class="loading-spinner">
          <i class="fas fa-spinner fa-spin"></i>
          <span>Chargement des produits...</span>
        </div>
      </div>
      
      <!-- Vue tableau -->
      <div v-else-if="viewMode === 'table'" class="table-view">
        <table class="products-table">
          <thead>
            <tr>
              <th @click="sort('designation')" class="sortable">
                Désignation
                <i class="fas fa-sort" :class="getSortIcon('designation')"></i>
              </th>
              <th>Catégorie</th>
              <th>IMEI/ICCID</th>
              <th @click="sort('statut')" class="sortable">
                Statut
                <i class="fas fa-sort" :class="getSortIcon('statut')"></i>
              </th>
              <th>Emplacement</th>
              <th @click="sort('quantite')" class="sortable">
                Quantité
                <i class="fas fa-sort" :class="getSortIcon('quantite')"></i>
              </th>
              <th 
                v-if="userRole === 'Admin'"
                @click="sort('prixVente')" 
                class="sortable"
              >
                Prix
                <i class="fas fa-sort" :class="getSortIcon('prixVente')"></i>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="product in paginatedProducts" :key="product._id" class="product-row">
              <td class="designation-cell">
                <div class="product-info">
                  <div class="product-name">{{ product.designation }}</div>
                  <div class="product-description" v-if="product.description">
                    {{ product.description }}
                  </div>
                </div>
              </td>
              <td :title="product.categorie">
                <span class="category-badge" :class="getCategoryClass(product.categorie)">
                  {{ product.categorie }}
                </span>
              </td>
              <td>
                <div class="identifiers">
                  <div v-if="product.imei" class="identifier">
                    <small>IMEI:</small> {{ product.imei }}
                  </div>
                  <div v-if="product.iccid" class="identifier">
                    <small>ICCID:</small> {{ product.iccid }}
                  </div>
                  <span v-if="!product.imei && !product.iccid" class="text-muted">-</span>
                </div>
              </td>
              <td :title="getStatusLabel(product)">
                <div class="status-container">
                  <span class="status-badge" :class="getStatusClass(product)">
                    {{ getStatusLabel(product) }}
                  </span>
                  <div v-if="product.rdvInfo" class="rdv-info" :title="`Mission ${product.rdvInfo.statut} - Technicien: ${product.rdvInfo.technicien?.nom} ${product.rdvInfo.technicien?.prenom}`">
                    <i class="fas fa-calendar-alt"></i>
                    <span class="rdv-status">{{ product.rdvInfo.statut }}</span>
                  </div>
                </div>
              </td>
              <td :title="formatLocation(product.emplacement, product.emplacementNom)">
                <span class="location">{{ formatLocation(product.emplacement, product.emplacementNom) }}</span>
              </td>
              <td class="quantity-cell" :title="`${product.quantite || 0} unité(s) en stock (Seuil: ${product.seuilAlerte || 5})`">
                <div class="quantity-info">
                  <span class="quantity-badge" :class="getQuantityClass(product)">
                    <i class="fas fa-boxes"></i>
                    {{ product.quantite || 0 }}
                  </span>
                </div>
              </td>
              <td v-if="userRole === 'Admin'" class="price-cell">
                <div class="price-info">
                  <div class="price">{{ formatPrice(product.prixVente) }}</div>
                  <div class="price-ttc">{{ formatPrice(product.prixVenteTTC) }} TTC</div>
                </div>
              </td>
              <td class="actions-cell">
                <div class="action-buttons">
                  <button @click="viewProduct(product)" class="btn-action btn-view" title="Voir">
                    <i class="fas fa-eye"></i>
                  </button>
                  <button @click="$emit('edit', product)" class="btn-action btn-edit" title="Modifier">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button @click="generateBarcode(product)" class="btn-action btn-barcode" title="Code-barres">
                    <i class="fas fa-barcode"></i>
                  </button>
                  <button 
                    v-if="(userRole === 'Magasinier' || userRole === 'Admin') && !['defectueux', 'perdu'].includes(product.statut)"
                    @click="openDeclarationModal(product)" 
                    class="btn-action btn-incident" 
                    title="Déclarer incident"
                  >
                    <i class="fas fa-exclamation-triangle"></i>
                  </button>
                  <button @click="$emit('delete', product)" class="btn-action btn-delete" title="Supprimer">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Vue cartes -->
      <div v-else-if="viewMode === 'cards'" class="cards-view">
        <div class="products-grid">
          <div v-for="product in paginatedProducts" :key="product._id" class="product-card">
            <div class="card-header">
              <div class="card-reference">
                <i class="fas fa-barcode"></i>
                {{ product.referenceInterne }}
              </div>
              <div class="card-actions">
                <button @click="viewProduct(product)" class="card-action-btn btn-view" title="Voir">
                  <i class="fas fa-eye"></i>
                </button>
                <button @click="$emit('edit', product)" class="card-action-btn btn-edit" title="Modifier">
                  <i class="fas fa-edit"></i>
                </button>
                <button @click="generateBarcode(product)" class="card-action-btn btn-barcode" title="Code-barres">
                  <i class="fas fa-barcode"></i>
                </button>
                <button 
                  v-if="(userRole === 'Magasinier' || userRole === 'Admin') && !['defectueux', 'perdu'].includes(product.statut)"
                  @click="openDeclarationModal(product)" 
                  class="card-action-btn btn-incident" 
                  title="Déclarer incident"
                >
                  <i class="fas fa-exclamation-triangle"></i>
                </button>
                <button @click="$emit('delete', product)" class="card-action-btn btn-delete" title="Supprimer">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
            
            <div class="card-body">
              <div class="card-title">{{ product.designation }}</div>
              <div class="card-description" v-if="product.description">
                {{ product.description }}
              </div>
              
              <div class="card-details">
                <div class="detail-row">
                  <span class="detail-label">Catégorie:</span>
                  <span class="category-badge" :class="getCategoryClass(product.categorie)">
                    {{ product.categorie }}
                  </span>
                </div>
                
                <div class="detail-row" v-if="product.imei">
                  <span class="detail-label">IMEI:</span>
                  <span class="detail-value identifier-text">{{ product.imei }}</span>
                </div>
                
                <div class="detail-row" v-if="product.iccid">
                  <span class="detail-label">ICCID:</span>
                  <span class="detail-value identifier-text">{{ product.iccid }}</span>
                </div>
                
                <div class="detail-row">
                  <span class="detail-label">Statut:</span>
                  <span class="status-badge" :class="getStatusClass(product)">
                    {{ getStatusLabel(product) }}
                  </span>
                </div>
                
                <div class="detail-row">
                  <span class="detail-label">Emplacement:</span>
                  <span class="detail-value">{{ formatLocation(product.emplacement, product.emplacementNom) }}</span>
                </div>
                
                <div class="detail-row">
                  <span class="detail-label">Quantité:</span>
                  <span class="quantity-badge" :class="getQuantityClass(product)">
                    <i class="fas fa-boxes"></i>
                    {{ product.quantite || 0 }}
                  </span>
                </div>
              </div>
            </div>
            
            <div v-if="userRole === 'Admin'" class="card-footer">
              <div class="card-price">
                <div class="price-main">{{ formatPrice(product.prixVente) }}</div>
                <div class="price-ttc">{{ formatPrice(product.prixVenteTTC) }} TTC</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Message si aucun produit -->
      <div v-if="!loading && filteredProducts.length === 0" class="no-data">
        <i class="fas fa-box-open"></i>
        <h3>Aucun produit trouvé</h3>
        <p>{{ searchQuery ? 'Aucun produit ne correspond à votre recherche.' : 'Commencez par ajouter votre premier produit.' }}</p>
        <button @click="$emit('create')" class="btn btn-primary">
          <i class="fas fa-plus"></i>
          Ajouter un produit
        </button>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination" v-if="totalPages > 1">
      <button 
        @click="currentPage = 1" 
        :disabled="currentPage === 1"
        class="pagination-btn"
      >
        <i class="fas fa-angle-double-left"></i>
      </button>
      <button 
        @click="currentPage--" 
        :disabled="currentPage === 1"
        class="pagination-btn"
      >
        <i class="fas fa-angle-left"></i>
      </button>
      
      <span class="pagination-info">
        Page {{ currentPage }} sur {{ totalPages }} 
        ({{ filteredProducts.length }} produits)
      </span>
      
      <button 
        @click="currentPage++" 
        :disabled="currentPage === totalPages"
        class="pagination-btn"
      >
        <i class="fas fa-angle-right"></i>
      </button>
      <button 
        @click="currentPage = totalPages" 
        :disabled="currentPage === totalPages"
        class="pagination-btn"
      >
        <i class="fas fa-angle-double-right"></i>
      </button>
    </div>

    <!-- Modale de détail produit -->
    <ProductDetailModal 
      v-if="showDetailModal"
      :product="selectedProduct"
      @close="closeDetailModal"
    />
    
    <!-- Modal de déclaration d'incident -->
    <DeclarationIncidentModal
      :show="showDeclarationModal"
      :product="productForDeclaration"
      :userRole="userRole"
      @close="closeDeclarationModal"
      @declaration-submitted="handleDeclarationSubmitted"
    />
    
    <!-- Modal d'ajout en lot (multi-IMEI/ICCID) -->
    <BulkProductModal
      v-if="showBulkModal"
      @close="showBulkModal = false"
      @save="handleBulkSave"
    />
    
    <!-- Panneau des déclarations en attente (Admin) -->
    <Transition name="slide-panel">
      <div v-if="showDeclarationsPanel && userRole === 'Admin'" class="declarations-panel">
        <div class="panel-header">
          <h3><i class="fas fa-exclamation-triangle"></i> Déclarations en attente</h3>
          <button @click="showDeclarationsPanel = false" class="close-panel">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="panel-body">
          <div v-if="pendingDeclarations.length === 0" class="empty-declarations">
            <i class="fas fa-check-circle"></i>
            <p>Aucune déclaration en attente</p>
          </div>
          <div v-else class="declarations-list">
            <div 
              v-for="(declaration, index) in pendingDeclarations" 
              :key="index" 
              class="declaration-card"
              :class="declaration.type"
            >
              <div class="declaration-header">
                <span class="declaration-type" :class="declaration.type">
                  <i :class="declaration.type === 'deteriore' ? 'fas fa-tools' : 'fas fa-exclamation-triangle'"></i>
                  {{ declaration.type === 'deteriore' ? 'Détérioré' : 'Volé' }}
                </span>
                <span class="declaration-date">{{ new Date(declaration.dateDeclaration).toLocaleDateString('fr-FR') }}</span>
              </div>
              <div class="declaration-product">
                <strong>{{ declaration.product?.designation }}</strong>
                <span class="product-ref">{{ declaration.product?.referenceInterne }}</span>
              </div>
              <div class="declaration-description">{{ declaration.description }}</div>
              <div class="declaration-actions">
                <button @click="validateDeclaration(declaration, 'valider')" class="btn-validate">
                  <i class="fas fa-check"></i> Valider
                </button>
                <button @click="validateDeclaration(declaration, 'rejeter')" class="btn-reject">
                  <i class="fas fa-times"></i> Rejeter
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted, onUnmounted } from 'vue'
import api from '@/services/api'
import { useStore } from 'vuex'
import { debounce } from 'lodash'
import * as XLSX from 'xlsx'
import ProductDetailModal from './ProductDetailModal.vue'
import DeclarationIncidentModal from './DeclarationIncidentModal.vue'
import BulkProductModal from './BulkProductModal.vue'

export default {
  name: 'ProductsManager',
  components: {
    ProductDetailModal,
    DeclarationIncidentModal,
    BulkProductModal
  },
  props: {
    products: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    selectedCategory: {
      type: String,
      default: null
    }
  },
  emits: ['refresh', 'create', 'edit', 'delete', 'give-products'],
  setup(props, { emit }) {
    // Store utilisateur
    const store = useStore()
    const user = computed(() => store.getters['auth/user'])
    const userRole = computed(() => user.value?.role || '')
    
    // État réactif
    const searchQuery = ref('')
    const filters = ref({
      categorie: '',
      statut: '',
      emplacement: ''
    })
    const categories = ref([])
    const loadingCategories = ref(false)

    // Watcher pour appliquer le filtre de catégorie depuis le parent
    watch(() => props.selectedCategory, (newCategory) => {
      if (newCategory) {
        filters.value.categorie = newCategory
      } else {
        filters.value.categorie = ''
      }
    }, { immediate: true })
    
    // Classes dynamiques pour les filtres
    const getCategorieClass = computed(() => {
      return filters.value.categorie ? 'has-selection' : ''
    })
    
    const getStatutClass = computed(() => {
      return filters.value.statut ? 'has-selection' : ''
    })
    const sortField = ref('dateCreation')
    const sortOrder = ref('desc')
    const currentPage = ref(1)
    const itemsPerPage = ref(20)
    const showDetailModal = ref(false)
    const selectedProduct = ref(null)
    const viewMode = ref('table') // 'table' ou 'cards'
    const isMobile = ref(window.innerWidth <= 768)
    
    // Déclarations d'incidents
    const showDeclarationModal = ref(false)
    const showDeclarationsPanel = ref(false)
    const productForDeclaration = ref(null)
    
    // Modal ajout en lot
    const showBulkModal = ref(false)
    const pendingDeclarationsCount = ref(0)
    const pendingDeclarations = ref([])
    
    // Ouvrir le modal de déclaration
    const openDeclarationModal = (product) => {
      productForDeclaration.value = product
      showDeclarationModal.value = true
    }
    
    // Fermer le modal de déclaration
    const closeDeclarationModal = () => {
      showDeclarationModal.value = false
      productForDeclaration.value = null
    }
    
    // Charger les déclarations en attente (Admin)
    const loadPendingDeclarations = async () => {
      if (userRole.value !== 'Admin') return
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/stock/declarations-incidents?statut=en_attente', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.ok) {
          const data = await response.json()
          pendingDeclarations.value = data.data || []
          pendingDeclarationsCount.value = pendingDeclarations.value.length
        }
      } catch (error) {
        console.error('Erreur chargement déclarations:', error)
      }
    }
    
    // Gérer la soumission d'une déclaration
    const handleDeclarationSubmitted = () => {
      closeDeclarationModal()
      emit('refresh')
      loadPendingDeclarations()
    }
    
    // Gérer la sauvegarde en lot (multi-IMEI/ICCID)
    const handleBulkSave = (result) => {
      showBulkModal.value = false
      emit('refresh')
      // Afficher un message de succès
      console.log(`✅ ${result.createdCount} produit(s) créé(s) avec succès`)
      if (result.skippedCount > 0) {
        console.log(`⚠️ ${result.skippedCount} identifiant(s) ignoré(s) (déjà existants)`)
      }
    }
    
    // Valider ou rejeter une déclaration (Admin)
    const validateDeclaration = async (declaration, action) => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch(`/api/stock/declaration-incident/${declaration.product._id}/${declaration._id}/valider`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ action })
        })
        if (response.ok) {
          emit('refresh')
          loadPendingDeclarations()
        }
      } catch (error) {
        console.error('Erreur validation déclaration:', error)
      }
    }

    // Détecter le mode mobile et forcer les cartes
    const handleResize = () => {
      isMobile.value = window.innerWidth <= 768
      if (isMobile.value && viewMode.value === 'table') {
        viewMode.value = 'cards'
      }
    }

    // Recherche avec debounce
    const debouncedSearch = debounce(() => {
      currentPage.value = 1
    }, 300)

    // Produits filtrés
    const filteredProducts = computed(() => {
      let result = [...props.products]

      // Recherche textuelle
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        result = result.filter(product => 
          product.referenceInterne.toLowerCase().includes(query) ||
          product.designation.toLowerCase().includes(query) ||
          (product.marque && product.marque.toLowerCase().includes(query)) ||
          (product.modele && product.modele.toLowerCase().includes(query)) ||
          (product.imei && product.imei.toLowerCase().includes(query)) ||
          (product.iccid && product.iccid.toLowerCase().includes(query))
        )
      }

      // Filtres
      if (filters.value.categorie) {
        result = result.filter(product => product.categorie === filters.value.categorie)
      }
      if (filters.value.statut) {
        result = result.filter(product => product.statut === filters.value.statut)
      }
      if (filters.value.emplacement) {
        result = result.filter(product => product.emplacement === filters.value.emplacement)
      }

      // Tri
      result.sort((a, b) => {
        let aVal = a[sortField.value]
        let bVal = b[sortField.value]
        
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase()
          bVal = bVal.toLowerCase()
        }
        
        if (sortOrder.value === 'asc') {
          return aVal > bVal ? 1 : -1
        } else {
          return aVal < bVal ? 1 : -1
        }
      })

      return result
    })

    // Produits paginés
    const paginatedProducts = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage.value
      const end = start + itemsPerPage.value
      return filteredProducts.value.slice(start, end)
    })

    // Nombre total de pages
    const totalPages = computed(() => {
      return Math.ceil(filteredProducts.value.length / itemsPerPage.value)
    })

    // Statistiques
    const stats = computed(() => {
      if (!props.products.length) return null
      
      return {
        total: props.products.length,
        enStock: props.products.filter(p => p.statut === 'en_stock').length,
        defectueux: props.products.filter(p => p.statut === 'defectueux').length
      }
    })

    // Méthodes
    const clearSearch = () => {
      searchQuery.value = ''
      currentPage.value = 1
    }

    const applyFilters = () => {
      currentPage.value = 1
    }

    const sort = (field) => {
      if (sortField.value === field) {
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
      } else {
        sortField.value = field
        sortOrder.value = 'asc'
      }
    }

    const getSortIcon = (field) => {
      if (sortField.value !== field) return ''
      return sortOrder.value === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
    }

    const getCategoryClass = (categorie) => {
      const classes = {
        'GPS': 'category-gps',
        'SIM': 'category-sim',
        'Accessoire': 'category-accessoire',
        'Câble': 'category-cable',
        'Capteur': 'category-capteur',
        'Alimentation': 'category-alimentation',
        'Divers': 'category-divers'
      }
      return classes[categorie] || 'category-default'
    }

    const getStatusClass = (product) => {
      // Utiliser le statutReel si disponible, sinon le statut normal
      const statut = product.statutReel || product.statut || product;
      
      const classes = {
        'en_stock': 'status-available',
        'affecte_technicien': 'status-assigned',
        'en_cours': 'status-intervention',
        'installe': 'status-installed',
        'retourne': 'status-returned',
        'defectueux': 'status-defective',
        'perdu': 'status-lost'
      }
      return classes[statut] || 'status-default'
    }

    const getStatusLabel = (product) => {
      // Utiliser le statutReel si disponible, sinon le statut normal
      const statut = product.statutReel || product.statut || product;
      
      const labels = {
        'en_stock': 'En stock',
        'affecte_technicien': 'Affecté technicien',
        'en_cours': 'En cours',
        'installe': 'Installé',
        'retourne': 'Retourné',
        'defectueux': 'Défectueux',
        'perdu': 'Perdu'
      }
      return labels[statut] || statut
    }

    const formatLocation = (emplacement, emplacementNom = null) => {
      // Si on a le nom enrichi du backend, l'utiliser en priorité
      if (emplacementNom) {
        return emplacementNom
      }
      
      if (!emplacement) return '-'
      
      // Formats technicien
      if (emplacement.startsWith('sac_technicien_') || 
          emplacement.startsWith('Technicien ') || 
          emplacement.startsWith('Tech:') ||
          emplacement.startsWith('Tech ')) {
        return 'Technicien'
      }
      
      // Formats client
      if (emplacement.startsWith('chez_client_') || 
          emplacement.startsWith('client_') ||
          emplacement.startsWith('Client ') ||
          emplacement.startsWith('Client:')) {
        return 'Chez Client'
      }
      
      // Magasin central
      if (emplacement === 'magasin_central' || emplacement === 'Magasin Central') {
        return 'Magasin Central'
      }
      
      return emplacement.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    const formatPrice = (price) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'TND'
      }).format(price || 0)
    }

    const getQuantityClass = (product) => {
      // Convertir explicitement en nombres pour éviter les problèmes de comparaison
      const quantite = Number(product.quantite) || 0
      const seuil = Number(product.seuilAlerte) || 5
      
      console.log(`🔍 Produit: ${product.designation}, Quantité: ${quantite} (${typeof quantite}), Seuil: ${seuil} (${typeof seuil})`)
      
      // Rouge : quantité inférieure au seuil
      if (quantite < seuil) {
        console.log('   → 🔴 Rouge (quantité < seuil)')
        return 'quantity-low'
      }
      
      // Jaune : quantité égale au seuil
      if (quantite === seuil) {
        console.log('   → 🟡 Jaune (quantité === seuil)')
        return 'quantity-medium'
      }
      
      // Vert : quantité supérieure au seuil
      console.log('   → 🟢 Vert (quantité > seuil)')
      return 'quantity-high'
    }

    const viewProduct = (product) => {
      selectedProduct.value = product
      showDetailModal.value = true
    }

    const closeDetailModal = () => {
      showDetailModal.value = false
      selectedProduct.value = null
    }

    const generateBarcode = (product) => {
      // TODO: Implémenter la génération de code-barres
      console.log('Génération code-barres pour:', product.referenceInterne)
    }

    const exportProducts = () => {
      try {
        // Préparer les données pour l'export
        const exportData = filteredProducts.value.map(product => {
          const baseData = {
            'Référence': product.referenceInterne || '',
            'Désignation': product.designation || '',
            'Description': product.description || '',
            'Catégorie': product.categorie || '',
            'Marque': product.marque || '',
            'Modèle': product.modele || '',
            'IMEI': product.imei || '',
            'ICCID': product.iccid || '',
            'Numéro de série': product.numeroSerie || '',
            'Statut': getStatusLabel(product),
            'Emplacement': formatLocation(product.emplacement || ''),
            'Quantité en stock': product.quantite || 0,
            'Date de création': product.dateCreation ? new Date(product.dateCreation).toLocaleDateString('fr-FR') : '',
            'Dernière modification': product.dateModification ? new Date(product.dateModification).toLocaleDateString('fr-FR') : ''
          }
          
          // Ajouter les prix seulement pour les Admins
          if (userRole.value === 'Admin') {
            baseData['Prix unitaire (TND)'] = product.prixVente || 0
            baseData['Prix TTC (TND)'] = product.prixVenteTTC || 0
          }
          
          return baseData
        })

        // Créer un nouveau workbook
        const wb = XLSX.utils.book_new()
        
        // Créer la feuille de calcul
        const ws = XLSX.utils.json_to_sheet(exportData)
        
        // Définir la largeur des colonnes selon le rôle
        const baseColWidths = [
          { wch: 15 }, // Référence
          { wch: 30 }, // Désignation
          { wch: 40 }, // Description
          { wch: 15 }, // Catégorie
          { wch: 15 }, // Marque
          { wch: 20 }, // Modèle
          { wch: 18 }, // IMEI
          { wch: 22 }, // ICCID
          { wch: 18 }, // Numéro de série
          { wch: 20 }, // Statut
          { wch: 20 }, // Emplacement
          { wch: 15 }, // Quantité en stock
          { wch: 18 }, // Date création
          { wch: 20 }  // Dernière modification
        ]
        
        // Ajouter les colonnes de prix pour les Admins
        const colWidths = [...baseColWidths]
        if (userRole.value === 'Admin') {
          colWidths.splice(-2, 0, 
            { wch: 18 }, // Prix unitaire
            { wch: 18 }  // Prix TTC
          )
        }
        ws['!cols'] = colWidths
        
        // Ajouter la feuille au workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Produits')
        
        // Générer le nom du fichier avec la date
        const now = new Date()
        const dateStr = now.toISOString().split('T')[0] // Format YYYY-MM-DD
        const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-') // Format HH-MM-SS
        const filename = `export_produits_${dateStr}_${timeStr}.xlsx`
        
        // Télécharger le fichier
        XLSX.writeFile(wb, filename)
        
        // Notification de succès
        console.log(`✅ Export Excel réussi: ${exportData.length} produits exportés`)
        
        // Vous pouvez ajouter une notification toast ici si disponible
        // toast.success(`✅ Export Excel réussi: ${exportData.length} produits exportés`)
        
      } catch (error) {
        console.error('❌ Erreur lors de l\'export Excel:', error)
        // toast.error('❌ Erreur lors de l\'export Excel')
      }
    }

    // Watchers
    watch(() => props.products, () => {
      if (currentPage.value > totalPages.value) {
        currentPage.value = 1
      }
    })

    // Charger les catégories depuis l'API
    const loadCategories = async () => {
      loadingCategories.value = true
      try {
        console.log('🔍 [ProductsManager] Chargement des catégories...')
        const response = await api.get('/categories', {
          params: { actif: true }
        })
        console.log('✅ [ProductsManager] Catégories reçues:', response.data)
        categories.value = response.data.data
        console.log('📦 [ProductsManager] Catégories chargées:', categories.value.length)
      } catch (error) {
        console.error('❌ [ProductsManager] Erreur chargement catégories:', error)
        console.error('Détails:', error.response?.data || error.message)
      } finally {
        loadingCategories.value = false
      }
    }

    onMounted(() => {
      loadCategories()
      loadPendingDeclarations()
      // Initialiser le mode responsive
      handleResize()
      window.addEventListener('resize', handleResize)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
    })

    return {
      // État
      searchQuery,
      filters,
      categories,
      loadingCategories,
      sortField,
      sortOrder,
      currentPage,
      itemsPerPage,
      showDetailModal,
      selectedProduct,
      viewMode,
      isMobile,
      
      // Déclarations d'incidents
      showDeclarationModal,
      showDeclarationsPanel,
      productForDeclaration,
      pendingDeclarationsCount,
      pendingDeclarations,
      
      // Modal ajout en lot
      showBulkModal,
      
      // Computed
      filteredProducts,
      paginatedProducts,
      totalPages,
      stats,
      
      // Méthodes
      debouncedSearch,
      clearSearch,
      applyFilters,
      sort,
      getSortIcon,
      getCategoryClass,
      getStatusClass,
      getStatusLabel,
      formatLocation,
      formatPrice,
      viewProduct,
      closeDetailModal,
      generateBarcode,
      exportProducts,
      getQuantityClass,
      userRole,
      openDeclarationModal,
      closeDeclarationModal,
      handleDeclarationSubmitted,
      handleBulkSave,
      loadPendingDeclarations,
      validateDeclaration,
      
      // Classes dynamiques
      getCategorieClass,
      getStatutClass
    }
  }
}
</script>

<style scoped>
.products-manager {
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 27, 183, 0.1);
  overflow: hidden;
  border: 1px solid rgba(0, 27, 183, 0.1);
}

/* Barre d'outils */
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
  border-bottom: 1px solid rgba(30, 60, 114, 0.1);
  flex-wrap: wrap;
  gap: 1rem;
  min-height: auto;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.search-box {
  position: relative;
  min-width: 300px;
}

.search-box i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
}

.search-input {
  width: 100%;
  padding: 12px 40px 12px 40px;
  border: 2px solid rgba(0, 27, 183, 0.2);
  border-radius: 12px;
  font-size: 14px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 4px rgba(0, 27, 183, 0.1);
  background: white;
}

.clear-search {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
}

.filters {
  display: flex;
  gap: 1rem;
  align-items: center;
}

/* Design professionnel pour les filtres */
.filter-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 251, 255, 0.95) 100%);
  border: 2px solid rgba(0, 27, 183, 0.15);
  border-radius: 16px;
  padding: 0;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 27, 183, 0.08);
  min-width: 200px;
}

.filter-wrapper:hover {
  border-color: rgba(0, 27, 183, 0.3);
  box-shadow: 0 8px 32px rgba(0, 27, 183, 0.15);
  transform: translateY(-2px);
}

.filter-wrapper:focus-within {
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 4px rgba(0, 27, 183, 0.1), 0 8px 32px rgba(0, 27, 183, 0.2);
  transform: translateY(-3px);
}

.filter-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  font-size: 0.9rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.filter-icon::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.2), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.filter-wrapper:hover .filter-icon::before {
  opacity: 1;
}

.premium-select {
  flex: 1;
  padding: 12px 16px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: var(--primary-blue);
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  letter-spacing: 0.3px;
  transition: all 0.3s ease;
}

.premium-select:focus {
  color: var(--secondary-blue);
}

.premium-select option {
  background: white;
  color: #2c3e50;
  padding: 12px 16px;
  font-weight: 500;
  border-radius: 8px;
  margin: 2px 0;
}

.premium-select option:hover {
  background: linear-gradient(135deg, #f8fbff 0%, #e3f2fd 100%);
  color: var(--primary-blue);
}

.premium-select option[value=""] {
  font-weight: 700;
  color: var(--primary-blue);
  background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
}

.filter-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 40px;
  color: var(--primary-blue);
  font-size: 0.8rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
}

.filter-wrapper:hover .filter-arrow {
  color: var(--secondary-blue);
  transform: translateY(-1px);
}

.filter-wrapper:focus-within .filter-arrow {
  transform: rotate(180deg);
}

/* Animation pour les options */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.premium-select:focus option {
  animation: slideDown 0.3s ease-out;
}

/* Effet glassmorphisme avancé */
.filter-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
  pointer-events: none;
  transition: opacity 0.3s ease;
  opacity: 0;
}

.filter-wrapper:hover::before {
  opacity: 1;
}

/* Indicateur de sélection active */
.filter-wrapper.has-selection {
  border-color: var(--primary-blue);
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.08) 0%, rgba(0, 70, 255, 0.08) 100%);
  box-shadow: 0 4px 20px rgba(0, 27, 183, 0.15), 0 0 0 1px rgba(0, 27, 183, 0.1);
}

.filter-wrapper.has-selection .filter-icon {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  animation: pulse 2s infinite;
  box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.3);
}

.filter-wrapper.has-selection .premium-select {
  color: var(--secondary-blue);
  font-weight: 700;
}

.filter-wrapper.has-selection .filter-arrow {
  color: var(--secondary-blue);
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.3), 0 0 0 0 rgba(40, 167, 69, 0.4);
  }
  70% {
    box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.3), 0 0 0 8px rgba(40, 167, 69, 0);
  }
  100% {
    box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.3), 0 0 0 0 rgba(40, 167, 69, 0);
  }
}

/* Badge de compteur pour les filtres actifs */
.filter-wrapper.has-selection::after {
  content: '1';
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
  color: white;
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(255, 107, 107, 0.4);
  animation: bounceIn 0.5s ease-out;
}

@keyframes bounceIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Effet de brillance au hover */
.filter-wrapper:hover::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  animation: shine 1.5s ease-in-out;
  pointer-events: none;
}

@keyframes shine {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

/* Styles spécifiques pour le filtre des catégories (Orange) */
.filter-categories {
  border-color: rgba(255, 152, 0, 0.15);
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.05) 0%, rgba(255, 193, 7, 0.05) 100%);
}

.filter-categories:hover {
  border-color: rgba(255, 152, 0, 0.3);
  box-shadow: 0 8px 32px rgba(255, 152, 0, 0.15);
}

.filter-categories:focus-within {
  border-color: #ff9800;
  box-shadow: 0 0 0 4px rgba(255, 152, 0, 0.1), 0 8px 32px rgba(255, 152, 0, 0.2);
}

.filter-categories .filter-icon {
  background: linear-gradient(135deg, #ff9800 0%, #ffc107 100%);
}

.filter-categories .premium-select {
  color: #ff9800;
}

.filter-categories .premium-select:focus {
  color: #f57c00;
}

.filter-categories .filter-arrow {
  color: #ff9800;
}

.filter-categories:hover .filter-arrow {
  color: #f57c00;
}

.filter-categories.has-selection {
  border-color: #ff9800;
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.08) 0%, rgba(255, 193, 7, 0.08) 100%);
  box-shadow: 0 4px 20px rgba(255, 152, 0, 0.15), 0 0 0 1px rgba(255, 152, 0, 0.1);
}

.filter-categories.has-selection .premium-select {
  color: #f57c00;
}

.filter-categories.has-selection .filter-arrow {
  color: #f57c00;
}

/* Styles spécifiques pour le filtre des statuts (Violet) */
.filter-status {
  border-color: rgba(156, 39, 176, 0.15);
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.05) 0%, rgba(142, 36, 170, 0.05) 100%);
}

.filter-status:hover {
  border-color: rgba(156, 39, 176, 0.3);
  box-shadow: 0 8px 32px rgba(156, 39, 176, 0.15);
}

.filter-status:focus-within {
  border-color: #9c27b0;
  box-shadow: 0 0 0 4px rgba(156, 39, 176, 0.1), 0 8px 32px rgba(156, 39, 176, 0.2);
}

.filter-status .filter-icon {
  background: linear-gradient(135deg, #9c27b0 0%, #8e24aa 100%);
}

.filter-status .premium-select {
  color: #9c27b0;
}

.filter-status .premium-select:focus {
  color: #7b1fa2;
}

.filter-status .filter-arrow {
  color: #9c27b0;
}

.filter-status:hover .filter-arrow {
  color: #7b1fa2;
}

.filter-status.has-selection {
  border-color: #9c27b0;
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.08) 0%, rgba(142, 36, 170, 0.08) 100%);
  box-shadow: 0 4px 20px rgba(156, 39, 176, 0.15), 0 0 0 1px rgba(156, 39, 176, 0.1);
}

.filter-status.has-selection .premium-select {
  color: #7b1fa2;
}

.filter-status.has-selection .filter-arrow {
  color: #7b1fa2;
}

/* Responsive pour les filtres */
@media (max-width: 768px) {
  .filters {
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
  }
  
  .filter-wrapper {
    min-width: auto;
    width: 100%;
  }
  
  .premium-select {
    font-size: 13px;
  }
  
  .filter-icon {
    width: 35px;
    height: 35px;
    font-size: 0.8rem;
  }
  
  .filter-arrow {
    width: 28px;
    height: 35px;
  }
}

.toolbar-right {
  display: flex;
  gap: 1rem;
  align-items: center;
}

/* Boutons de vue */
.view-toggle {
  display: flex;
  background: rgba(0, 27, 183, 0.1);
  border-radius: 12px;
  padding: 4px;
  gap: 2px;
}

.view-btn {
  padding: 8px 12px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  color: var(--primary-blue);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 16px;
}

.view-btn:hover {
  background: rgba(0, 27, 183, 0.1);
}

.view-btn.active {
  background: var(--primary-blue);
  color: white;
  box-shadow: 0 2px 8px rgba(0, 27, 183, 0.3);
}


/* Conteneur principal */
.content-container {
  position: relative;
  min-height: 400px;
}

/* Vue tableau avec scroll professionnel */
.table-view {
  overflow-x: auto;
  overflow-y: visible;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 27, 183, 0.1);
  background: white;
}

.table-view::-webkit-scrollbar {
  height: 8px;
}

.table-view::-webkit-scrollbar-track {
  background: #f1f3f4;
  border-radius: 4px;
}

.table-view::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 4px;
}

.table-view::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, var(--secondary-blue), var(--primary-blue));
}

/* Vue cartes */
.cards-view {
  padding: 2rem;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  max-width: 100%;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: var(--primary-blue);
}

.loading-spinner i {
  font-size: 3rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.products-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

/* Design compact pour afficher toutes les colonnes */
.products-table {
  min-width: 1200px; /* Augmenté pour inclure la colonne Quantité */
}

.products-table th:nth-child(1), /* Référence */
.products-table td:nth-child(1) {
  width: 100px;
  min-width: 100px;
}

.products-table th:nth-child(2), /* Désignation */
.products-table td:nth-child(2) {
  width: 180px;
  min-width: 180px;
}

.products-table th:nth-child(3), /* Catégorie */
.products-table td:nth-child(3) {
  width: 90px;
  min-width: 90px;
}

/* Colonne Marque/Modèle supprimée */

.products-table th:nth-child(4), /* IMEI/ICCID */
.products-table td:nth-child(4) {
  width: 140px;
  min-width: 140px;
}

.products-table th:nth-child(5), /* Statut */
.products-table td:nth-child(5) {
  width: 100px;
  min-width: 100px;
}

.products-table th:nth-child(6), /* Emplacement */
.products-table td:nth-child(6) {
  width: 110px;
  min-width: 110px;
}

.products-table th:nth-child(7), /* Quantité */
.products-table td:nth-child(7) {
  width: 80px;
  min-width: 80px;
}

.products-table th:nth-child(8), /* Prix */
.products-table td:nth-child(8) {
  width: 90px;
  min-width: 90px;
}

.products-table th:nth-child(9), /* Actions */
.products-table td:nth-child(9) {
  width: 130px;
  min-width: 130px;
}

.products-table th {
  background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
  padding: 0.6rem 0.4rem;
  text-align: left;
  font-weight: 700;
  border-bottom: 2px solid rgba(0, 27, 183, 0.1);
  white-space: nowrap;
  color: var(--primary-blue);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 27, 183, 0.1);
}

.products-table th.sortable {
  cursor: pointer;
  user-select: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.products-table th.sortable:hover {
  background: linear-gradient(135deg, #e3f2fd 0%, #f8fbff 100%);
  transform: translateY(-2px);
}

.products-table th i {
  margin-left: 0.5rem;
  opacity: 0.5;
}

.products-table th i.fa-sort-up,
.products-table th i.fa-sort-down {
  opacity: 1;
  color: var(--primary-blue);
}

.products-table td {
  padding: 0.5rem 0.4rem;
  border-bottom: 1px solid #e9ecef;
  vertical-align: middle;
  font-size: 0.8rem;
  line-height: 1.3;
  color: #2c3e50;
  font-weight: 500;
}

/* Gestion spécifique des colonnes */
.products-table td:nth-child(1) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.products-table td:nth-child(2) {
  white-space: normal;
  word-wrap: break-word;
  line-height: 1.3;
}

.products-table td:nth-child(3),
.products-table td:nth-child(4),
.products-table td:nth-child(5),
.products-table td:nth-child(6),
.products-table td:nth-child(7),
.products-table td:nth-child(8),
.products-table td:nth-child(9) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.products-table td:nth-child(10) {
  white-space: nowrap;
}

.product-row {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 3px solid transparent;
}

.product-row:hover {
  background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
  border-left: 3px solid var(--primary-blue);
  box-shadow: 0 2px 8px rgba(0, 27, 183, 0.1);
  transform: translateX(2px);
}

/* Cellules spécifiques */
.reference-cell strong {
  color: var(--primary-blue);
  font-family: 'Courier New', monospace;
}

.product-info {
  width: 100%;
}

.product-name {
  font-weight: 700;
  margin-bottom: 0.2rem;
  font-size: 0.85rem;
  color: var(--primary-blue);
  line-height: 1.2;
}

.product-description {
  font-size: 0.7rem;
  color: #6c757d;
  line-height: 1.3;
  max-height: 2.6em;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-top: 0.1rem;
}

.identifiers {
  font-size: 0.7rem;
  font-family: 'Courier New', monospace;
  width: 100%;
}

.identifier {
  margin-bottom: 0.1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0.1rem 0;
}

.identifier small {
  color: #6c757d;
  font-weight: 700;
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.price-info {
  text-align: right;
}

.price {
  font-weight: 700;
  color: #28a745;
  font-size: 0.8rem;
}

.price-ttc {
  font-size: 0.7rem;
  color: #6c757d;
  font-weight: 600;
  margin-top: 0.1rem;
}

/* Styles pour la colonne Quantité */
.quantity-cell {
  text-align: center;
}

.quantity-info {
  display: flex;
  justify-content: center;
  align-items: center;
}

.quantity-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.6rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: default;
  min-width: 50px;
  justify-content: center;
}

.quantity-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.quantity-badge i {
  font-size: 0.7rem;
}

/* Classes de quantité selon le stock */
.quantity-high {
  background: linear-gradient(135deg, #d4edda, #c3e6cb);
  color: #155724;
  border: 1px solid #28a745;
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.2);
}

.quantity-medium {
  background: linear-gradient(135deg, #fff3cd, #ffeaa7);
  color: #856404;
  border: 1px solid #ffc107;
  box-shadow: 0 2px 4px rgba(255, 193, 7, 0.2);
}

.quantity-low {
  background: linear-gradient(135deg, #f8d7da, #f1b0b7);
  color: #721c24;
  border: 1px solid #dc3545;
  box-shadow: 0 2px 4px rgba(220, 53, 69, 0.2);
}

.quantity-empty {
  background: linear-gradient(135deg, #343a40, #495057);
  color: white;
  border: 1px solid #6c757d;
  box-shadow: 0 2px 4px rgba(52, 58, 64, 0.3);
}

/* Badges avec design professionnel */
.category-badge, .status-badge {
  display: inline-block;
  padding: 0.25rem 0.4rem;
  border-radius: 6px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;
  min-width: 60px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: default;
}

.category-badge:hover, .status-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Badges catégories avec design professionnel */
.category-gps { 
  background: linear-gradient(135deg, #e3f2fd, #bbdefb); 
  color: #1976d2; 
  border: 1px solid #90caf9;
  font-weight: 700;
}
.category-sim { 
  background: linear-gradient(135deg, #f3e5f5, #e1bee7); 
  color: #7b1fa2; 
  border: 1px solid #ce93d8;
  font-weight: 700;
}
.category-accessoire { 
  background: linear-gradient(135deg, #e8f5e8, #c8e6c9); 
  color: #388e3c; 
  border: 1px solid #a5d6a7;
  font-weight: 700;
}
.category-cable { 
  background: linear-gradient(135deg, #fff3e0, #ffe0b2); 
  color: #f57c00; 
  border: 1px solid #ffcc02;
  font-weight: 700;
}
.category-capteur { 
  background: linear-gradient(135deg, #fce4ec, #f8bbd9); 
  color: #c2185b; 
  border: 1px solid #f48fb1;
  font-weight: 700;
}
.category-alimentation { 
  background: linear-gradient(135deg, #e0f2f1, #b2dfdb); 
  color: #00796b; 
  border: 1px solid #80cbc4;
  font-weight: 700;
}
.category-divers { 
  background: linear-gradient(135deg, #f5f5f5, #e0e0e0); 
  color: #616161; 
  border: 1px solid #bdbdbd;
  font-weight: 700;
}

/* Container pour statut et info RDV */
.status-container {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: flex-start;
}

.rdv-info {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.1), rgba(0, 86, 179, 0.1));
  color: #0056b3;
  padding: 0.15rem 0.4rem;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 500;
  border: 1px solid rgba(0, 123, 255, 0.2);
}

.rdv-info i {
  font-size: 0.6rem;
}

.rdv-status {
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Badges statuts avec design professionnel */
.status-available { 
  background: linear-gradient(135deg, #d4edda, #c3e6cb); 
  color: #155724; 
  border: 1px solid #28a745;
  font-weight: 700;
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.2);
}
.status-assigned { 
  background: linear-gradient(135deg, #cce5ff, #a6d5fa); 
  color: #004085; 
  border: 1px solid #007bff;
  font-weight: 700;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2);
}
.status-intervention { 
  background: linear-gradient(135deg, #fff3cd, #ffeaa7); 
  color: #856404; 
  border: 1px solid #ffc107;
  font-weight: 700;
  box-shadow: 0 2px 4px rgba(255, 193, 7, 0.2);
}
.status-installed { 
  background: linear-gradient(135deg, #d1ecf1, #b3d9e0); 
  color: #0c5460; 
  border: 1px solid #17a2b8;
  font-weight: 700;
  box-shadow: 0 2px 4px rgba(23, 162, 184, 0.2);
}
.status-returned { 
  background: linear-gradient(135deg, #f8d7da, #f1b0b7); 
  color: #721c24; 
  border: 1px solid #dc3545;
  font-weight: 700;
  box-shadow: 0 2px 4px rgba(220, 53, 69, 0.2);
}
.status-defective { 
  background: linear-gradient(135deg, #f5c6cb, #f1959b); 
  color: #721c24; 
  border: 1px solid #dc3545;
  font-weight: 700;
  box-shadow: 0 2px 4px rgba(220, 53, 69, 0.2);
}
.status-lost { 
  background: linear-gradient(135deg, #343a40, #495057); 
  color: white; 
  border: 1px solid #6c757d;
  font-weight: 700;
  box-shadow: 0 2px 4px rgba(52, 58, 64, 0.3);
}

/* Actions avec design professionnel */
.actions-cell {
  white-space: nowrap;
}

.action-buttons {
  display: flex;
  gap: 0.2rem;
  justify-content: center;
}

.btn-action {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.btn-view { 
  background: linear-gradient(135deg, #17a2b8, #20c997); 
  color: white; 
  border: 1px solid #17a2b8;
}
.btn-edit { 
  background: linear-gradient(135deg, #ffc107, #ffdb4d); 
  color: #212529; 
  border: 1px solid #ffc107;
}
.btn-barcode { 
  background: linear-gradient(135deg, #6c757d, #868e96); 
  color: white; 
  border: 1px solid #6c757d;
}
.btn-delete { 
  background: linear-gradient(135deg, #dc3545, #e55a6b); 
  color: white; 
  border: 1px solid #dc3545;
}

.btn-incident {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  border: 1px solid #f59e0b;
}

.btn-incident:hover {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  border-color: #ef4444;
}

.btn-action:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
}

.btn-action:active {
  transform: translateY(-1px) scale(1.02);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
}

/* Boutons généraux */
.btn {
  padding: 0.5rem 0.875rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: all 0.3s ease;
  text-decoration: none;
  white-space: nowrap;
  height: 36px;
}

.btn-primary {
  background: linear-gradient(135deg, #001BB7 0%, #0046FF 100%);
  color: white;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #0015A3 0%, #003DE6 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.3);
}

.btn-bulk {
  background: linear-gradient(135deg, #001BB7 0%, #0046FF 100%);
  color: white;
  border: none;
}

.btn-bulk:hover {
  background: linear-gradient(135deg, #0015A3 0%, #003DE6 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.4);
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-export {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border: 1px solid #28a745;
  position: relative;
  overflow: hidden;
}

.btn-export::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  transition: left 0.5s ease;
}

.btn-export:hover::before {
  left: 100%;
}

.btn-export:hover {
  background: linear-gradient(135deg, #20c997 0%, #17a2b8 100%);
  border-color: #20c997;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Message aucune donnée */
.no-data {
  text-align: center;
  padding: 4rem 2rem;
  color: #6c757d;
}

.no-data i {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.no-data h3 {
  margin-bottom: 0.5rem;
  color: #495057;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  gap: 0.5rem;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.pagination-btn {
  width: 40px;
  height: 40px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  margin: 0 1rem;
  font-size: 0.9rem;
  color: #6c757d;
}

/* Design responsive compact - toutes colonnes visibles */
@media (max-width: 1300px) {
  .products-table {
    min-width: 1000px;
  }
  
  .products-table th,
  .products-table td {
    padding: 0.4rem 0.3rem;
    font-size: 0.75rem;
  }
  
  .products-table th {
    font-size: 0.7rem;
  }
}

@media (max-width: 1200px) {
  .products-table {
    min-width: 1000px;
  }
  
  /* Colonne Marque/Modèle supprimée */
}

@media (max-width: 1100px) {
  .products-table {
    min-width: 900px;
  }
  
  .products-table th:nth-child(6), /* Emplacement */
  .products-table td:nth-child(6) {
    display: none;
  }
}

@media (max-width: 1000px) {
  .products-table {
    min-width: 800px;
  }
  
  .products-table th:nth-child(4), /* IMEI/ICCID */
  .products-table td:nth-child(4) {
    display: none;
  }
}

@media (max-width: 900px) {
  .products-table {
    min-width: 700px;
  }
  
  .products-table th:nth-child(7), /* Quantité */
  .products-table td:nth-child(7) {
    display: none;
  }
}

@media (max-width: 800px) {
  .products-table {
    min-width: 600px;
  }
  
  .products-table th:nth-child(8), /* Prix */
  .products-table td:nth-child(8) {
    display: none;
  }
}

@media (max-width: 768px) {
  .table-view {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    border-radius: 8px;
  }
  
  .products-table {
    min-width: 500px;
  }
  
  .products-table th,
  .products-table td {
    padding: 0.4rem 0.25rem;
    font-size: 0.7rem;
  }
  
  .products-table th {
    font-size: 0.65rem;
    padding: 0.5rem 0.25rem;
  }
  
  .product-name {
    font-size: 0.75rem;
  }
  
  .product-description {
    font-size: 0.65rem;
  }
  
  .action-buttons {
    flex-direction: row;
    gap: 0.15rem;
  }
  
  .btn-action {
    width: 24px;
    height: 24px;
    font-size: 0.7rem;
  }
  
  .category-badge, .status-badge {
    padding: 0.2rem 0.3rem;
    font-size: 0.6rem;
    min-width: 50px;
  }
}

/* Utilitaires et améliorations */
.text-muted {
  color: #6c757d !important;
  font-style: italic;
}

/* Tooltips natifs améliorés */
[title] {
  cursor: help;
}

/* Animation d'entrée pour les lignes */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.product-row {
  animation: fadeInUp 0.3s ease-out;
}

/* Indicateur de scroll */
.table-view::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 100%;
  background: linear-gradient(to left, rgba(0, 27, 183, 0.1), transparent);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.table-view:hover::after {
  opacity: 1;
}

.brand-model {
  line-height: 1.3;
}

.brand {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.75rem;
  margin-bottom: 0.05rem;
}

.model {
  font-size: 0.7rem;
  color: #6c757d;
  font-style: italic;
}

.location {
  font-size: 0.75rem;
  color: #495057;
  font-weight: 500;
}

/* Styles pour les cartes de produits */
.product-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 27, 183, 0.1);
  border: 1px solid rgba(0, 27, 183, 0.1);
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.product-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 60px rgba(0, 27, 183, 0.2);
  border-color: rgba(0, 27, 183, 0.2);
}

.card-header {
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.card-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1), transparent);
}

.card-reference {
  font-family: 'Courier New', monospace;
  font-weight: 700;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  z-index: 1;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  position: relative;
  z-index: 1;
}

.card-action-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(255, 255, 255, 0.2);
  color: white;
  backdrop-filter: blur(10px);
}

.card-action-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.card-body {
  padding: 2rem;
}

.card-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--primary-blue);
  margin-bottom: 0.5rem;
  line-height: 1.3;
}

.card-description {
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 1.5rem;
}

.card-details {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(0, 27, 183, 0.1);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 600;
  color: var(--primary-blue);
  font-size: 0.9rem;
  min-width: 100px;
}

.detail-value {
  color: #495057;
  font-weight: 500;
  text-align: right;
}

.identifier-text {
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  background: rgba(0, 27, 183, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
}

.card-footer {
  background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
  padding: 1.5rem 2rem;
  border-top: 1px solid rgba(0, 27, 183, 0.1);
}

.card-price {
  text-align: center;
}

.price-main {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-blue);
  margin-bottom: 0.25rem;
}

.price-ttc {
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
}

/* Responsive Mobile - Forcer les cartes */
@media (max-width: 768px) {
  .toolbar {
    flex-direction: column;
    align-items: stretch;
    padding: 1rem;
    gap: 0.75rem;
  }
  
  .toolbar-left {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .toolbar-right {
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;
  }
  
  .search-box {
    min-width: auto;
    width: 100%;
  }
  
  .search-input {
    padding: 10px 35px;
    font-size: 14px;
  }
  
  /* Cacher le toggle de vue sur mobile - forcer les cartes */
  .view-toggle {
    display: none;
  }
  
  /* Cacher le tableau sur mobile */
  .table-view {
    display: none;
  }
  
  /* Styles des cartes mobile */
  .cards-view {
    padding: 0.75rem;
  }
  
  .products-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .product-card {
    border-radius: 12px;
  }
  
  .card-header {
    padding: 1rem;
  }
  
  .card-reference {
    font-size: 0.95rem;
  }
  
  .card-actions {
    gap: 0.35rem;
  }
  
  .card-action-btn {
    width: 32px;
    height: 32px;
    font-size: 0.85rem;
  }
  
  .card-body {
    padding: 1rem;
  }
  
  .card-title {
    font-size: 1.1rem;
    margin-bottom: 0.35rem;
  }
  
  .card-description {
    font-size: 0.85rem;
    margin-bottom: 1rem;
  }
  
  .card-details {
    gap: 0.5rem;
  }
  
  .detail-row {
    padding: 0.5rem 0;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .detail-label {
    font-size: 0.8rem;
    min-width: auto;
  }
  
  .detail-value {
    font-size: 0.85rem;
  }
  
  .category-badge, .status-badge {
    font-size: 0.7rem;
    padding: 0.3rem 0.5rem;
  }
  
  .quantity-badge {
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
  }
  
  .card-footer {
    padding: 1rem;
  }
  
  .price-main {
    font-size: 1.2rem;
  }
  
  .price-ttc {
    font-size: 0.8rem;
  }
  
  /* Boutons toolbar mobile */
  .btn {
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
  }
  
  .btn-export,
  .btn-secondary,
  .btn-primary,
  .btn-bulk {
    flex: 1;
    justify-content: center;
    min-width: 0;
  }
  
  /* Pagination mobile */
  .pagination {
    padding: 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .pagination-btn {
    width: 36px;
    height: 36px;
  }
  
  .pagination-info {
    width: 100%;
    text-align: center;
    order: -1;
    margin: 0 0 0.5rem 0;
    font-size: 0.85rem;
  }
  
  /* No data mobile */
  .no-data {
    padding: 2rem 1rem;
  }
  
  .no-data i {
    font-size: 3rem;
  }
  
  .no-data h3 {
    font-size: 1.1rem;
  }
  
  .no-data p {
    font-size: 0.9rem;
  }
}

/* Très petits écrans */
@media (max-width: 480px) {
  .toolbar {
    padding: 0.75rem;
  }
  
  .toolbar-right {
    flex-direction: column;
  }
  
  .btn-export,
  .btn-secondary,
  .btn-primary,
  .btn-bulk {
    width: 100%;
  }
  
  .card-header {
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-start;
  }
  
  .card-actions {
    width: 100%;
    justify-content: flex-end;
  }
  
  .detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
  
  .detail-value {
    text-align: left;
  }
}

/* ===== STYLES DÉCLARATIONS D'INCIDENTS ===== */

/* Bouton Warning */
.btn-warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  border: none;
  position: relative;
  height: 36px;
}

.btn-warning:hover {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.btn-warning.active {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.badge-count {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #ef4444;
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
  animation: pulse-badge 2s infinite;
}

@keyframes pulse-badge {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

/* Bouton incident dans les actions */
.btn-incident {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.btn-incident:hover {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  transform: translateY(-2px) scale(1.1);
}

/* Panneau des déclarations */
.declarations-panel {
  position: fixed;
  top: 0;
  right: 0;
  width: 420px;
  max-width: 100%;
  height: 100vh;
  background: white;
  box-shadow: -10px 0 50px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
}

.slide-panel-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.slide-panel-leave-active {
  transition: all 0.3s ease-in;
}

.slide-panel-enter-from,
.slide-panel-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.panel-header {
  padding: 20px 24px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
}

.close-panel {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.close-panel:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.empty-declarations {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
}

.empty-declarations i {
  font-size: 60px;
  color: #10b981;
  margin-bottom: 16px;
}

.empty-declarations p {
  font-size: 1rem;
  margin: 0;
}

.declarations-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.declaration-card {
  background: #f9fafb;
  border-radius: 16px;
  padding: 16px;
  border-left: 4px solid;
  transition: all 0.3s;
}

.declaration-card.deteriore {
  border-left-color: #f59e0b;
}

.declaration-card.vole {
  border-left-color: #ef4444;
}

.declaration-card:hover {
  transform: translateX(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.declaration-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.declaration-type {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.declaration-type.deteriore {
  background: #fef3c7;
  color: #92400e;
}

.declaration-type.vole {
  background: #fee2e2;
  color: #991b1b;
}

.declaration-date {
  font-size: 0.8rem;
  color: #6b7280;
}

.declaration-product {
  margin-bottom: 8px;
}

.declaration-product strong {
  display: block;
  font-size: 0.95rem;
  color: #1f2937;
}

.declaration-product .product-ref {
  font-size: 0.8rem;
  color: #6b7280;
}

.declaration-description {
  font-size: 0.85rem;
  color: #4b5563;
  margin-bottom: 12px;
  line-height: 1.4;
}

.declaration-actions {
  display: flex;
  gap: 8px;
}

.btn-validate,
.btn-reject {
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;
}

.btn-validate {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.btn-validate:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

.btn-reject {
  background: #f3f4f6;
  color: #6b7280;
}

.btn-reject:hover {
  background: #fee2e2;
  color: #991b1b;
}
</style>
